import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scan, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex flex-col justify-center px-6 py-16" data-testid="forgot-password-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-auto">
        <Link to="/login" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mute hover:text-slate-900 transition-colors duration-300 mb-12" data-testid="forgot-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        <div className="flex items-center gap-2 mb-8">
          <span className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
            <Scan className="w-4.5 h-4.5 text-white" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-xl">X-GEN.AI</span>
        </div>

        {sent ? (
          <div className="border border-brand/40 bg-brand/5 p-8" data-testid="forgot-success">
            <MailCheck className="w-8 h-8 text-brand mb-6" />
            <h1 className="font-display text-2xl font-bold tracking-tight mb-3">Check your inbox</h1>
            <p className="text-mute text-sm leading-relaxed mb-6">
              If an account exists for <span className="text-white">{email}</span>, a password reset link is on its way. It expires in 1 hour.
            </p>
            <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-brand hover:text-[#0F172A] transition-colors duration-300" data-testid="forgot-success-login-link">
              Return to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Reset access</h1>
            <p className="text-mute text-sm mb-10">Enter your account email and we'll send you a secure reset link.</p>

            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="forgot-email">Email</label>
                <Input id="forgot-email" data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@hospital.org" required className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
              </div>

              {error && <p className="text-pulse text-sm font-mono" data-testid="forgot-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-form-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending..." : "Send reset link"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
